/**
 * Кастомные критерии (#857) — запросы и ответы `/api/v1/call-criteria`.
 *
 * Определение критерия живёт в `@aica/core/call-criteria` (его же читает шаг
 * оценки); здесь — только форма API поверх него. Потолки длин и число
 * вариантов — из `CALL_CRITERION_LIMITS`, повторять их числами нельзя.
 *
 * Спека: docs/plans/2026-09-18-custom-criteria.md §3.1, §8.
 */

import { z } from 'zod/v4';
import {
  BUILTIN_CRITERIA_CATALOG,
  CALL_CRITERION_CHANNELS,
  CALL_CRITERION_DIRECTIONS,
  CALL_CRITERION_LIMITS,
  CALL_CRITERION_PAUSE_REASONS,
  MAX_EVIDENCE_PER_ANSWER,
  RECORD_CRITERION_STATES,
  CallCriterionOptionSchema,
  CallCriterionWordListSchema,
  StoredCallCriterionOptionSchema,
  callCriterionTextFields,
} from '@aica/core/call-criteria';
import { ANALYSIS_LANGUAGES } from '@aica/core/languages';
import {
  PURCHASE_INTENT_CATEGORIES,
  analyzedCallCategorySchema,
  type AnalyzedCallCategory,
} from './records';

const distinct = (values: readonly string[]): boolean =>
  new Set(values).size === values.length;

/**
 * Граница размера тела, а не продуктовый потолок: список проектов области и
 * хранимых вариантов (с архивными) ограничен, чтобы разбор запроса оставался
 * дешёвым. Потолок НЕАРХИВНЫХ вариантов (`CALL_CRITERION_LIMITS.options`)
 * проверяет правило правки (`planCriterionEdit`). Экспортирован для формы:
 * она не должна давать выбрать то, что запрос отвергнет (#861).
 */
export const CALL_CRITERION_REQUEST_LIST_MAX = 100;

/** Непустой список без повторов или `null` — «без ограничения». */
const scopeList = <T extends z.ZodType<string>>(item: T) =>
  z
    .array(item)
    .min(1)
    .max(CALL_CRITERION_REQUEST_LIST_MAX)
    .refine(distinct, 'values must not repeat')
    .nullable();

export const callCriterionScopeSchema = z
  .object({
    categories: scopeList(analyzedCallCategorySchema),
    projectIds: scopeList(z.string().min(1).max(64)),
    channels: scopeList(z.enum(CALL_CRITERION_CHANNELS)),
    /**
     * Направление звонка (#868, Э1). Необязательное: конструктор UI о нём ещё
     * не знает. При создании отсутствие — «без ограничения»; при правке —
     * «не трогать», иначе правка области из такого UI стирала бы ограничение,
     * поставленное сервером (встроенный критерий только для исходящих).
     * Снимает ограничение явный `null`.
     *
     * Список из ВСЕХ направлений приводится к `null`: «оба» — не ограничение.
     * Сохранённый как список, он молча выбрасывал бы звонок с неизвестным
     * направлением, которого «без ограничения» не теряет (`criterionAppliesTo`).
     */
    directions: scopeList(z.enum(CALL_CRITERION_DIRECTIONS))
      .transform((list) =>
        list !== null && CALL_CRITERION_DIRECTIONS.every((d) => list.includes(d))
          ? null
          : list
      )
      .optional(),
  })
  .strict();

// .strict(): схема варианта в core лишние поля молча срезает, а запрос должен
// их отвергать — `archived` при создании означает непонятый клиентом контракт.
const createOptions = z
  .array(CallCriterionOptionSchema.strict())
  .min(1)
  .max(CALL_CRITERION_LIMITS.options)
  .refine(
    (options) => distinct(options.map((o) => o.code)),
    'option codes must not repeat'
  );

const createCommon = {
  ...callCriterionTextFields,
  scope: callCriterionScopeSchema.optional(),
};

/**
 * Создание кастомного критерия. Ключа в запросе нет: его генерирует сервер,
 * и `.strict()` отвергнет попытку передать свой.
 */
export const createCallCriterionRequestSchema = z.discriminatedUnion('resultType', [
  z
    .object({
      ...createCommon,
      resultType: z.literal('flag'),
      looksFor: z.enum(['presence', 'absence']),
    })
    .strict(),
  z.object({ ...createCommon, resultType: z.literal('score') }).strict(),
  z
    .object({ ...createCommon, resultType: z.literal('choice'), options: createOptions })
    .strict(),
]);

/**
 * Правка. Всё необязательно. `resultType` в схеме нет: тип ответа после
 * создания не меняется (план части 2), и `.strict()` отвергнет его как лишнее
 * поле. Варианты передаются полным списком, с признаком архива; допустимость
 * перехода (коды не удаляются, потолок неархивных) решает `planCriterionEdit`.
 */
export const updateCallCriterionRequestSchema = z
  .object({
    name: callCriterionTextFields.name.optional(),
    question: callCriterionTextFields.question.optional(),
    guidance: callCriterionTextFields.guidance.unwrap().nullable().optional(),
    looksFor: z.enum(['presence', 'absence']).optional(),
    options: z
      .array(StoredCallCriterionOptionSchema.strict())
      .min(1)
      .max(CALL_CRITERION_REQUEST_LIST_MAX)
      .optional(),
    scope: callCriterionScopeSchema.optional(),
    isActive: z.boolean().optional(),
    /**
     * Полный список слов встроенного `forbidden_words` (#2784): пустой —
     * очистить. Другим критериям маршрут его не принимает (422
     * `word_list_not_allowed`). Пробелы по краям, пустые строки и повторы без
     * учёта регистра схема убирает сама.
     */
    wordList: CallCriterionWordListSchema.optional(),
  })
  .strict();

/**
 * Предел области встроенного критерия — шире него маршрут правки область не
 * примет (409 `builtin_scope_beyond_catalog`, план Э2, решение 10), и форма
 * шире не предлагает: каналы — не шире записи каталога, категории у критерия
 * «только покупательские» (`tcpa_compliant`) — не шире
 * `PURCHASE_INTENT_CATEGORIES`. `null` в измерении — предела нет; `null`
 * целиком — у ключа пределов нет (или ключ не из каталога).
 */
export interface BuiltinScopeLimits {
  channels: readonly (typeof CALL_CRITERION_CHANNELS)[number][] | null;
  categories: readonly AnalyzedCallCategory[] | null;
}

export function builtinScopeLimits(
  key: string | null | undefined
): BuiltinScopeLimits | null {
  const entry = BUILTIN_CRITERIA_CATALOG.find((candidate) => candidate.key === key);
  if (!entry) return null;
  const limits: BuiltinScopeLimits = {
    channels: entry.channels,
    categories: entry.purchaseIntentOnly ? PURCHASE_INTENT_CATEGORIES : null,
  };
  return limits.channels || limits.categories ? limits : null;
}

export const callCriterionResponseSchema = z.object({
  id: z.string(),
  key: z.string(),
  origin: z.enum(['builtin', 'custom']),
  name: z.string(),
  question: z.string(),
  guidance: z.string().nullable(),
  resultType: z.enum(['flag', 'score', 'choice']),
  looksFor: z.enum(['presence', 'absence']).nullable(),
  options: z.array(StoredCallCriterionOptionSchema).nullable(),
  /**
   * Список слов встроенного `forbidden_words` (#2784); у остальных `null`.
   * Необязательное, как `pauseReason`: ответ откатанного edge-api без поля.
   */
  wordList: z.array(z.string()).nullable().optional(),
  scope: z.object({
    categories: z.array(z.string()).nullable(),
    projectIds: z.array(z.string()).nullable(),
    channels: z.array(z.enum(CALL_CRITERION_CHANNELS)).nullable(),
    // Необязательное: ответ откатанного edge-api, который поля не знает,
    // обязан разбираться и в более новом UI. Отсутствие = `null`.
    directions: z.array(z.enum(CALL_CRITERION_DIRECTIONS)).nullable().optional(),
  }),
  isActive: z.boolean(),
  /**
   * Почему критерий на паузе (#2520, план Э2, решение 10): `catalog` и
   * `jurisdiction` — пауза Aelo, клиент её не снимает; `profile` — выключенный
   * тумблер профиля (встроенный, до Э2 или засеянный так); `no_words` — у
   * «запрещённых слов» пуст список (#2784), снимается сохранением списка;
   * `customer` — клиент. У включённого — `null`. Необязательное (решение 13): ответ
   * откатанного edge-api без поля UI читает как «причина неизвестна».
   */
  pauseReason: z.enum(CALL_CRITERION_PAUSE_REASONS).nullable().optional(),
  archivedAt: z.string().nullable(),
  version: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const listCallCriteriaResponseSchema = z.object({
  criteria: z.array(callCriterionResponseSchema),
  /**
   * Архивных больше, чем отдано: список несёт только последние по времени
   * архива. Без `includeArchived` — всегда `false`.
   */
  archivedTruncated: z.boolean(),
  /** Тарифный лимит: `used` — кастомные без архивных, встроенные не считаются. */
  usage: z.object({ used: z.number().int(), limit: z.number().int() }),
});

export const getCallCriterionResponseSchema = z.object({
  criterion: callCriterionResponseSchema,
});

/**
 * Результат одного критерия на конкретном звонке — то, что рисует группа
 * «Критерии» в панели записи и зеркалящий её раздел PDF (спека §7, #862).
 *
 * Состояние вынесено отдельным полем, а не выведено из значений: спека требует
 * три различимых состояния («в разговоре не найдено», «неприменимо», «не
 * оценено»), и «значение пустое» их не различает. Сопоставление с хранением:
 * `applicable: false` → `not_applicable`, `unsure: true` → `not_found`,
 * строки нет вовсе → `not_evaluated`, иначе → `answered`.
 */
export const recordCriterionStateSchema = z.enum(RECORD_CRITERION_STATES);

export const recordCriterionEvidenceSchema = z.object({
  /** Номер реплики транскрипта — по нему панель прыгает к моменту. */
  turn: z.number().int(),
  quote: z.string(),
  speaker: z.enum(['agent', 'client', 'unknown']),
});

/**
 * `looksFor` («что ищем» у флажка) в ответе НЕТ намеренно: ни панель, ни PDF его
 * не читают, а поле, которое никто не рендерит, только выглядит как контракт
 * (находка `/code-review` на #1121). Полярность критерия видна в его настройках;
 * если она понадобится строке результата, поле вернётся вместе с тем, что его
 * показывает.
 */
export const recordCriterionResultSchema = z.object({
  criterionId: z.string(),
  name: z.string(),
  resultType: z.enum(['flag', 'score', 'choice']),
  state: recordCriterionStateSchema,
  valueBool: z.boolean().nullable(),
  valueScore: z.number().int().nullable(),
  valueCode: z.string().nullable(),
  /**
   * Подпись выбранного варианта списка. Код — служебное значение, показывать
   * его человеку нельзя; подпись берётся из текущего определения критерия,
   * включая архивные варианты (коды не удаляются, `planCriterionEdit`).
   */
  valueLabel: z.string().nullable(),
  triggered: z.boolean().nullable(),
  evidence: z.array(recordCriterionEvidenceSchema),
  note: z.string().nullable(),
  /** `null` у `not_evaluated`: оценки не было. */
  evaluatedAt: z.string().nullable(),
  /**
   * Происхождение и ключ критерия — только у организации с Э2 встроенных
   * (#2518; план `docs/plans/2026-10-18-builtin-criteria-e2.md`, решения 8 и
   * 13). Необязательные: ответ откатанного edge-api их не знает.
   */
  origin: z.enum(['builtin', 'custom', 'demo']).optional(),
  key: z.string().optional(),
});

/**
 * Откуда результат встроенного на записи (порядок a–e читателя Э2): `answer` —
 * строка в группе «Критерии», `legacy` — старая строка анализа, `none` —
 * ни той, ни другой.
 */
export const builtinResultSourceSchema = z.enum(['answer', 'legacy', 'none']);

/**
 * Запрос критериев записи. `analysisId` — анализ, который рисует панель
 * (`record.analysisId`): источники встроенных читаются у него, а не у текущего
 * анализа PG, который переразбор мог продвинуть раньше, чем панель получила
 * новые детали записи (#2582). `none` — панель знает, что у записи анализа ещё
 * нет: текущий анализ PG может быть не опубликован в D1 (первый прогон), и
 * встроенные не читаются вовсе. Нет параметра — текущий анализ, как раньше.
 */
export const recordCriteriaQuerySchema = z.object({
  analysisId: z.union([z.string().uuid(), z.literal('none')]).optional(),
});

export const recordCriteriaResponseSchema = z.object({
  success: z.literal(true),
  criteria: z.array(recordCriterionResultSchema),
  /** Разбор ещё идёт — значения появятся позже, это не «нет критериев». */
  processing: z.boolean().optional(),
  status: z.string().optional(),
  /**
   * Источник результата по ключу встроенного на этой записи — только у
   * организации с Э2. Панель и PDF показывают старую строку ключа (уведомление
   * о записи, TCPA, конфиденциальность, мат, коды обязательных фраз) только
   * при `legacy`; поля нет — Э2 выключен, старые строки как раньше. Ключ —
   * строкой, не перечислением: новый встроенный приходит с edge-api раньше UI.
   */
  builtinSources: z.record(z.string(), builtinResultSourceSchema).optional(),
  /**
   * Ключи встроенных, которые СЕЙЧАС на паузе Aelo (каталог), — только вместе с
   * `builtinSources`. Старая строка такого ключа без ответа в группе остаётся с
   * подписью «на паузе Aelo до повторного замера», как в карточке Compliance
   * Results (#2517): источник `none` иначе прятал бы вердикт без замены
   * (слово владельца 19.10, #2518).
   */
  builtinPausedByAelo: z.array(z.string()).optional(),
});

// ============================================================================
// Проверка формулировки на последних звонках (#861, спека §5)
// ============================================================================

/**
 * Запрос проверки: черновик — ТОЙ ЖЕ схемой, что создание. У «проверить» и
 * «сохранить» не должно быть двух представлений о допустимом критерии.
 *
 * Язык заметок — только из словаря языков анализа (`@aica/core/languages`):
 * мусор на границе API — это 400, а не молчаливая подмена. Сама граница от
 * внедрения в промпт стоит там, где код языка становится текстом
 * (`renderOutputLanguage` в `@aica/prompt-builder` сводит незнакомый код к
 * «языку разговора»), — эта схема её не заменяет, а только отвечает клиенту
 * честной ошибкой. `'auto'` (по умолчанию) — «на языке разговора»: выборка
 * охватывает проекты с разными языками анализа, единственно верного значения у
 * неё нет.
 */
/** Встроенные, чью пробу ведут под их ключом: со списком слов или с `evidenceSpeaker`. */
export const CRITERIA_TRIAL_BUILTIN_KEYS = [
  'forbidden_words',
  'agent_profanity',
  'data_privacy_violation',
] as const;
export type CriteriaTrialBuiltinKey = (typeof CRITERIA_TRIAL_BUILTIN_KEYS)[number];

export const criteriaTrialRequestSchema = z
  .object({
    draft: createCallCriterionRequestSchema,
    outputLanguage: z.enum(ANALYSIS_LANGUAGES).optional(),
    /**
     * Список слов, с которым проверить встроенный `forbidden_words` (#2784):
     * без него модель не знает, что искать. Формулировку встроенного правит
     * не клиент, но проверить список до сохранения он может.
     */
    wordList: CallCriterionWordListSchema.optional(),
    /**
     * Признак: черновик — это встроенный критерий с правилом заземления
     * (#2826, #2821). Ключ заземления берётся отсюда, а не из наличия списка, и
     * проба встроенного отвечает по тем же правилам, что рабочая оценка
     * (`evidenceSpeaker`). `wordList` без признака встроенного со списком
     * отвергается 422 `word_list_not_allowed`, как при сохранении обычного
     * флажка (`trialWordListRefusal` в core). Сверка с каталогом — тест
     * в workflows-api.
     */
    builtinKey: z.enum(CRITERIA_TRIAL_BUILTIN_KEYS).optional(),
  })
  .strict();

/** Сколько записей уходит в одну проверку. Спека §5: «последние 20 звонков». */
export const CRITERIA_TRIAL_SAMPLE_SIZE = 20;

/**
 * Запрос edge-api → workflows-api: клиентский запрос плюс то, что добавляет
 * edge. ВНУТРЕННИЙ контракт (клиенту не виден), но живёт здесь же: обе стороны
 * одного вызова описаны одной схемой, и edge собирает тело по её типу, так что
 * расхождение «что шлёт edge / что принимает workflows» — ошибка компиляции.
 * Организация — из сессии edge, в клиентском запросе её нет.
 */
export const criteriaTrialServiceRequestSchema = criteriaTrialRequestSchema.extend({
  orgId: z.string().min(1),
  limit: z.number().int().min(1).max(CRITERIA_TRIAL_SAMPLE_SIZE).optional(),
  /** Fallback, когда биндинг не донёс `X-Trace-Id`. */
  traceId: z.string().optional(),
});

/** Почему проверка не состоялась целиком — законные состояния настройки, не ошибки. */
export const CRITERIA_TRIAL_REFUSALS = [
  'flag_off',
  'over_ceiling',
  'empty_sample',
] as const;

/**
 * Почему у записи выборки нет ответа. Исходы оценки плюс четыре своих у проверки:
 * `answer_rejected` (модель ответила не тем типом значения), `unredactable`
 * (редакция ПДн отказалась отдавать запись модели — вызова не было, и это
 * состояние записи, а не поломка, #2308), `error` (оценка не завершилась,
 * неизвестно даже, был ли вызов), `deadline` (до записи не дошли — кончился
 * бюджет нажатия, вызова не было).
 */
export const CRITERIA_TRIAL_RECORD_SKIPS = [
  'flag_off',
  'no_criteria',
  'no_transcript',
  'already_current',
  'provider_failed',
  'answer_rejected',
  'unredactable',
  'error',
  'deadline',
] as const;

export const criteriaTrialAnswerSchema = z.object({
  applicable: z.boolean(),
  unsure: z.boolean(),
  valueBool: z.boolean().nullable(),
  valueScore: z.number().int().nullable(),
  valueCode: z.string().nullable(),
  triggered: z.boolean().nullable(),
  evidence: z.array(recordCriterionEvidenceSchema),
  note: z.string().nullable(),
});

export const criteriaTrialRecordSchema = z.object({
  recordId: z.string(),
  createdAt: z.string().nullable(),
  channel: z.string(),
  category: z.string().nullable(),
  /**
   * Анализ, на котором отвечала проверка (его транскрипт и роли), — отметка
   * правки несёт его обратно (`criteriaReviseMarkSchema.analysisId`, #2112).
   * Необязательный: workflows-api, выкаченный раньше этого поля, его не
   * отдаёт, и разбор его ответа не должен из-за этого падать.
   */
  analysisId: z.string().optional(),
  /** `null` — ответа на этой записи нет; почему, говорит `skipped`. */
  answer: criteriaTrialAnswerSchema.nullable(),
  /**
   * Незнакомый исход записи читается как `error`, а не роняет разбор целиком.
   *
   * Строгий словарь здесь стоил бы всех ответов нажатия: одна запись с исходом,
   * которого клиентская схема ещё не знает, превращала бы в 503 до двадцати
   * ответов, за которые платформа уже заплатила, — и слот потолка, и повтор
   * клиента, который упрётся в то же самое (`/code-review`). Конфиденциальности
   * строгость не добавляет: себестоимость — это незнакомое ПОЛЕ, а их
   * `z.object` срезает в любом случае; незнакомое ЗНАЧЕНИЕ исхода денег не
   * несёт. Сам дрейф ловится раньше — компиляцией: workflows-api берёт тип
   * исхода отсюда.
   */
  skipped: z.enum(CRITERIA_TRIAL_RECORD_SKIPS).optional().catch('error'),
});

/**
 * Итог проверки для КЛИЕНТА.
 *
 * Себестоимости здесь нет намеренно: внутренний итог workflows-api несёт
 * `costUsd` — это счёт провайдера, то есть COGS, а COGS видит только владелец
 * платформы (#1148, PR #1169). Цена нажатия, которую человек обязан видеть
 * (спека §5), измеряется ВЫЗОВАМИ — `calls`, и оплаченный сбой тоже вызов.
 * Схема — обычный `z.object`, поэтому разбор ответа workflows-api этой схемой
 * срезает `costUsd` сам; edge-api отдаёт клиенту именно результат разбора.
 * Трата проверки видна владельцу в админ-отчёте расходов (тип `criteria_trial`).
 */
export const criteriaTrialResponseSchema = z.object({
  refused: z.enum(CRITERIA_TRIAL_REFUSALS).optional(),
  records: z.array(criteriaTrialRecordSchema),
  totals: z.object({
    /** Сколько записей попало в выборку. */
    sampled: z.number().int().nonnegative(),
    /** Сколько платных вызовов состоялось — цена нажатия. */
    calls: z.number().int().nonnegative(),
    /** Сколько записей дало ответ. */
    answered: z.number().int().nonnegative(),
    /** Сколько оценок не завершилось. */
    failed: z.number().int().nonnegative(),
  }),
});

/**
 * Сколько edge-api ждёт workflows-api на одном нажатии. Клиент ждёт чуть
 * дольше — его предел стоит за этим (тот же порядок, что у ассистента).
 *
 * Выше худшего случая той стороны, а не «с запасом на глаз»: бюджет нажатия
 * там 70 с, после него не начинается ни одна пачка, а начатая кончается не
 * позже предела вызова (25 с), плюс чтение выборки и кредов — меньше 100 с.
 * Предел ниже этого обрывал бы нажатие, чьи вызовы уже оплачены, и клиент не
 * увидел бы ответов, за которые мы заплатили.
 */
export const CRITERIA_TRIAL_PROXY_DEADLINE_MS = 110_000;

// ============================================================================
// Помощник «своими словами» и правка по примерам (#1416, части 2–3)
// ============================================================================

/**
 * Формулировка критерия без области — то, что пишет помощник и что
 * возвращает правка. Поля и строгость — те же, что у создания: черновик,
 * который форма не смогла бы сохранить, клиенту не показывается (спека).
 */
export const criterionWordingSchema = z.discriminatedUnion('resultType', [
  z
    .object({
      ...callCriterionTextFields,
      resultType: z.literal('flag'),
      looksFor: z.enum(['presence', 'absence']),
    })
    .strict(),
  z.object({ ...callCriterionTextFields, resultType: z.literal('score') }).strict(),
  z
    .object({
      ...callCriterionTextFields,
      resultType: z.literal('choice'),
      options: createOptions,
    })
    .strict(),
]);

/** Сколько символов намерения принимает помощник (спека: «до ~500»). */
export const CRITERION_INTENT_MAX = 500;
/** Причина отметки «ответ неверный» — необязательная, своими словами. */
export const CRITERION_MARK_REASON_MAX = 200;
/** Сколько отметок уходит в одну правку: фрагмент на каждую — в одном промпте. */
export const CRITERION_REVISE_MARKS_MAX = 10;
/** Фраза «что изменено» в ответе правки. */
export const CRITERION_REVISE_CHANGE_MAX = 300;

/**
 * Почему помощник или правка не позвали модель: возможность выключена
 * (`flag_off`, fail closed) или потолок организации (`over_ceiling`,
 * недоступный счётчик — тоже он). Законные состояния, не ошибки — как у
 * проверки.
 */
export const CRITERIA_ASSIST_REFUSALS = ['flag_off', 'over_ceiling'] as const;

/**
 * Почему черновика нет, хотя модель звали: `unclear` — модель ответила, что из
 * описания не выходит критерий, наблюдаемый в разговоре; `invalid` — ответ не
 * прошёл схему формулировки (у помощника — дважды подряд). Человеку оба — «не
 * получилось, напишите иначе». Сбой самого провайдера (нет ответа, ошибка,
 * предел времени) сюда не входит: он не про текст клиента, и маршрут отвечает
 * 503 `assist_failed` — «повторите».
 */
export const CRITERIA_ASSIST_FAILURES = ['unclear', 'invalid'] as const;

/** Неудачи правки без `unredactable`: список отказанных отметок есть у него одного. */
const REVISE_FAILURES_WITHOUT_LIST = [...CRITERIA_ASSIST_FAILURES, 'reanalyzed'] as const;

/**
 * Неудачи правки: те же, что у помощника, и `unredactable` — редакция ПДн
 * отказалась отдавать модели разговор отмеченной записи (#2429);
 * `reanalyzed` — отмеченный звонок переанализировали после проверки, и её
 * ответ относится к другому анализу (#2112): проверить заново.
 * Окончательный исход, а не сбой: те же отметки откажут так же при каждом
 * повторе, поэтому это тело 200, а не 503, который клиент повторял бы.
 */
export const CRITERIA_REVISE_FAILURES = [
  ...REVISE_FAILURES_WITHOUT_LIST,
  'unredactable',
] as const;

const assistRefused = z.object({
  status: z.literal('refused'),
  reason: z.enum(CRITERIA_ASSIST_REFUSALS),
});

const assistFailed = z.object({
  status: z.literal('failed'),
  reason: z.enum(CRITERIA_ASSIST_FAILURES),
});

const reviseFailed = z.discriminatedUnion('reason', [
  z.object({
    status: z.literal('failed'),
    reason: z.enum(REVISE_FAILURES_WITHOUT_LIST),
  }),
  // `recordIds` — только у `unredactable`: у другой причины список отказанных
  // отметок был бы утверждением, которого она не делает, и разбор его отбросит.
  z.object({
    status: z.literal('failed'),
    reason: z.literal('unredactable'),
    /**
     * `recordId` отметок, чей разговор редакция не отдала модели (#2474), —
     * ровно те, что клиент сам прислал в запросе, без текста. Без них отказ
     * одной записи из десяти отмеченных заставлял бы подбирать, какую отметку
     * снять. Нет у ответа без списка (прежний ответ) — тогда клиент говорит
     * общим текстом.
     */
    recordIds: z
      .array(z.string().min(1).max(64))
      .min(1)
      .max(CRITERION_REVISE_MARKS_MAX)
      .optional(),
  }),
]);

/**
 * Запрос помощника — одно намерение своими словами. Язык черновика выбирает
 * модель по тексту клиента (иначе — язык организации); организация — из сессии.
 */
export const criteriaDraftRequestSchema = z
  .object({ intent: z.string().trim().min(1).max(CRITERION_INTENT_MAX) })
  .strict();

export const criteriaDraftServiceRequestSchema = criteriaDraftRequestSchema.extend({
  orgId: z.string().min(1),
  /** Fallback, когда биндинг не донёс `X-Trace-Id`. */
  traceId: z.string().optional(),
});

export const criteriaDraftResponseSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('ready'), wording: criterionWordingSchema }),
  assistRefused,
  assistFailed,
]);

/**
 * Отмеченный ответ проверки — без текста. Цитаты и заметку клиент не
 * присылает: фрагменты разговора сервер берёт сам из той же выборки, что
 * проверка, и перередактирует (спека, часть 3), а текст, пришедший от клиента,
 * был бы непроверенным «фрагментом разговора» в промпте. Номера реплик — те,
 * что проверка вернула в `evidence[].turn`.
 */
export const criteriaReviseMarkSchema = z
  .object({
    recordId: z.string().min(1).max(64),
    /**
     * Анализ, на котором отвечала проверка (`criteriaTrialRecordSchema`):
     * номера реплик и роли относятся к его транскрипту и метаданным, и правка
     * по другому анализу той же записи отказывает (`reanalyzed`, #2112). Пока необязательный —
     * схема строгая, и обязательное поле отвергло бы отметки вкладки, открытой
     * до выкатки; станет обязательным следующим шагом.
     */
    analysisId: z.string().min(1).max(64).optional(),
    answer: z
      .object({
        applicable: z.boolean(),
        unsure: z.boolean(),
        valueBool: z.boolean().nullable(),
        valueScore: z.number().int().min(0).max(100).nullable(),
        valueCode: z.string().max(64).nullable(),
        evidenceTurns: z
          .array(z.number().int().min(1))
          .max(MAX_EVIDENCE_PER_ANSWER)
          .refine(
            (turns) => new Set(turns).size === turns.length,
            'turns must not repeat'
          ),
      })
      .strict(),
    reason: z.string().trim().max(CRITERION_MARK_REASON_MAX).optional(),
  })
  .strict();

/**
 * Запрос правки: черновик — ТОЙ ЖЕ схемой, что создание и проверка (это та
 * формулировка, которую проверяли), и отметки по звонкам её выборки.
 */
export const criteriaReviseRequestSchema = z
  .object({
    draft: createCallCriterionRequestSchema,
    marks: z
      .array(criteriaReviseMarkSchema)
      .min(1)
      .max(CRITERION_REVISE_MARKS_MAX)
      .refine(
        (marks) => distinct(marks.map((mark) => mark.recordId)),
        'records must not repeat'
      ),
  })
  .strict();

export const criteriaReviseServiceRequestSchema = criteriaReviseRequestSchema.extend({
  orgId: z.string().min(1),
  traceId: z.string().optional(),
});

/**
 * Ответ правки. `record_not_found` — отказ отдельный от `refused`: edge
 * отвечает на него 404 без различия «чужая запись / нет такой / нет текущего
 * анализа» (спека, «Безопасность»), а клиенту он не приходит телом.
 */
export const criteriaReviseResponseSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('ready'),
    wording: criterionWordingSchema,
    change: z.string().trim().min(1).max(CRITERION_REVISE_CHANGE_MAX),
  }),
  assistRefused,
  reviseFailed,
]);

export const criteriaReviseServiceResponseSchema = z.union([
  criteriaReviseResponseSchema,
  z.object({ status: z.literal('record_not_found') }),
]);

/**
 * Сколько edge-api ждёт помощника и правку. Худший случай той стороны —
 * помощник: два вызова по 30 с (повтор невалидного ответа), плюс чтение
 * флага, тарифа и кредов; у правки вызов один, но перед ним чтение выборки.
 */
export const CRITERIA_ASSIST_PROXY_DEADLINE_MS = 80_000;

// ============================================================================
// Как отвечали критерии за 7 дней (#861, план §4.2)
// ============================================================================

/** Окно строки конструктора — скользящие сутки по дате звонка. */
export const CRITERIA_ACTIVITY_WINDOW_DAYS = 7;

const count = z.number().int().nonnegative();

/**
 * Счётчики одного критерия за окно — только текущая версия критерия. Доли
 * считает читатель: здесь нет деления, которое спрятало бы пустой знаменатель.
 * `values` — ответы списка по кодам; `not_mentioned` в них не входит, он в
 * `notMentioned` («в разговоре не найдено», как в панели звонка).
 */
export const criterionActivitySchema = z.object({
  criterionId: z.string(),
  /**
   * Версия, по которой считали. Читатель показывает счётчики только рядом с
   * той же версией: после правки прежняя доля описывала бы другую формулировку.
   */
  version: z.number().int(),
  evaluated: count,
  notApplicable: count,
  unsure: count,
  notMentioned: count,
  triggered: count,
  scored: count,
  scoreSum: count,
  values: z.array(z.object({ code: z.string(), count })),
});

/**
 * Запись на каждый живой критерий организации, и без оценок в окне — нулями:
 * по версии в ней читатель отличает «оценок этой формулировки нет» от
 * счётчиков другой версии.
 */
export const criteriaActivityResponseSchema = z.object({
  windowDays: z.number().int().positive(),
  criteria: z.array(criterionActivitySchema),
});

export type CriterionWording = z.infer<typeof criterionWordingSchema>;
export type CriteriaAssistRefusal = (typeof CRITERIA_ASSIST_REFUSALS)[number];
export type CriteriaAssistFailure = (typeof CRITERIA_ASSIST_FAILURES)[number];
export type CriteriaReviseFailure = (typeof CRITERIA_REVISE_FAILURES)[number];
export type CriteriaDraftRequest = z.infer<typeof criteriaDraftRequestSchema>;
export type CriteriaDraftServiceRequest = z.input<
  typeof criteriaDraftServiceRequestSchema
>;
export type CriteriaDraftResponse = z.infer<typeof criteriaDraftResponseSchema>;
export type CriteriaReviseMark = z.infer<typeof criteriaReviseMarkSchema>;
export type CriteriaReviseRequest = z.infer<typeof criteriaReviseRequestSchema>;
export type CriteriaReviseServiceRequest = z.input<
  typeof criteriaReviseServiceRequestSchema
>;
export type CriteriaReviseResponse = z.infer<typeof criteriaReviseResponseSchema>;
export type CriteriaReviseServiceResponse = z.infer<
  typeof criteriaReviseServiceResponseSchema
>;
export type CriteriaTrialRequest = z.infer<typeof criteriaTrialRequestSchema>;
export type CriterionActivity = z.infer<typeof criterionActivitySchema>;
export type CriteriaActivityResponse = z.infer<typeof criteriaActivityResponseSchema>;
export type CriteriaTrialServiceRequest = z.input<
  typeof criteriaTrialServiceRequestSchema
>;
export type CriteriaTrialRefusal = (typeof CRITERIA_TRIAL_REFUSALS)[number];
export type CriteriaTrialRecordSkip = (typeof CRITERIA_TRIAL_RECORD_SKIPS)[number];
export type CriteriaTrialAnswer = z.infer<typeof criteriaTrialAnswerSchema>;
export type CriteriaTrialRecord = z.infer<typeof criteriaTrialRecordSchema>;
export type CriteriaTrialResponse = z.infer<typeof criteriaTrialResponseSchema>;

export type CallCriterionScopeRequest = z.infer<typeof callCriterionScopeSchema>;
export type RecordCriterionState = z.infer<typeof recordCriterionStateSchema>;
export type RecordCriterionEvidence = z.infer<typeof recordCriterionEvidenceSchema>;
export type RecordCriterionResult = z.infer<typeof recordCriterionResultSchema>;
export type RecordCriteriaResponse = z.infer<typeof recordCriteriaResponseSchema>;
export type BuiltinResultSources = NonNullable<RecordCriteriaResponse['builtinSources']>;
export type CreateCallCriterionRequest = z.infer<typeof createCallCriterionRequestSchema>;
export type UpdateCallCriterionRequest = z.infer<typeof updateCallCriterionRequestSchema>;
export type CallCriterionResponse = z.infer<typeof callCriterionResponseSchema>;
export type ListCallCriteriaResponse = z.infer<typeof listCallCriteriaResponseSchema>;
